import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import {
  createEmptyCustomerOrderLine,
  createEmptyCustomerOrderManualLine,
} from '#/modules/crmMgmt/customerOrder/helper/formSerialize';
import {
  CUSTOMER_ORDER_LINES_TIP,
  CUSTOMER_ORDER_MANUAL_LINES_TIP,
} from '#/modules/crmMgmt/customerOrder/schemas/customerOrderSchema';

/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[2.5rem_minmax(20rem,1fr)_minmax(8rem,8rem)_minmax(7rem,7rem)_minmax(7rem,7rem)_minmax(10rem,10rem)_minmax(10rem,10rem)_minmax(10rem,10rem)_minmax(16rem,16rem)_4rem]';

const MANUAL_LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[2.5rem_minmax(16rem,1fr)_minmax(6rem,6rem)_minmax(7rem,7rem)_minmax(7rem,7rem)_minmax(10rem,10rem)_minmax(14rem,14rem)_4rem]';
/* eslint-enable max-len */

const LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[106rem]',
);

const MANUAL_LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[70rem]',
);

const PANEL_MAX_H = 'max-h-[calc(100vh-var(--form-list-chrome))]';

const LINE_GRID_CLASS = cn(
  'min-w-full gap-3 px-3',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const MANUAL_LINE_GRID_CLASS = cn(
  'min-w-full gap-3 px-3',
  '@3xl/form-section:grid',
  MANUAL_LINE_GRID_COLS,
);

const LINE_HEADER_CLASS = cn(
  LINE_GRID_CLASS,
  'hidden border-b border-gray-300 bg-gray-100 py-2',
  'text-xs font-medium text-gray-600',
);

const MANUAL_LINE_HEADER_CLASS = cn(
  MANUAL_LINE_GRID_CLASS,
  'hidden border-b border-gray-300 bg-gray-100 py-2',
  'text-xs font-medium text-gray-600',
);

const LINE_ROW_CLASS = cn(
  LINE_GRID_CLASS,
  'grid grid-cols-1 py-2',
  '@3xl/form-section:items-center',
);

const MANUAL_LINE_ROW_CLASS = cn(
  MANUAL_LINE_GRID_CLASS,
  'grid grid-cols-1 py-2',
  '@3xl/form-section:items-center',
);

/** 手機短欄兩欄；≥@3xl 以 contents 讓子格進表頭橫列 grid */
const MOBILE_FIELD_PAIR_CLASS = cn(
  'grid min-w-0 grid-cols-2 gap-3',
  '@3xl/form-section:contents',
);

const FIELD_CELL_CLASS = 'min-w-0';

const MOBILE_HALF_CELL_CLASS = cn(
  'min-w-0 w-1/2',
  '@3xl/form-section:w-auto',
);

/** 手機欄位小標；mb-1 與輸入框留距（≥@3xl 隱藏） */
const MOBILE_FIELD_LABEL_CLASS = cn(
  'mb-1 text-xs text-gray-500',
  '@3xl/form-section:hidden',
);

/** 同列 Input 對齊 SelectCore（minHeight 3.125rem）；僅本編輯器，不改全域 Input */
const LINE_INPUT_CLASS = cn(
  'h-[3.125rem] min-h-[3.125rem] w-full',
);

/** 手機約 2～3 行；≥@3xl 單列高對齊 SelectCore／LINE_INPUT、不可拖曳改高 */
const LINE_REMARK_TEXTAREA_CLASS = cn(
  'min-h-16 w-full resize-y',
  '@3xl/form-section:h-[3.125rem] @3xl/form-section:min-h-[3.125rem]',
  '@3xl/form-section:resize-none @3xl/form-section:overflow-y-auto',
  '@3xl/form-section:py-1 @3xl/form-section:field-sizing-fixed',
);

const DISCOUNT_RATE_TIP = (
  <div className="space-y-1">
    <p>折數填「成交價倍率」，範圍 0～1：</p>
    <ul className="m-0 list-disc space-y-0.5 pl-4">
      <li>九折填 0.9、八五折填 0.85</li>
      <li>不打折填 1 或留空</li>
      <li>不要填折扣比例（九折不是 0.1）</li>
    </ul>
    <p>折數與「每件折扣」只能擇一填寫。</p>
  </div>
);

const DiscountRateLabel = ({ children }) => (
  <span className="inline-flex items-center gap-1">
    {children}
    <ActionHintIcon tip={DISCOUNT_RATE_TIP} ariaLabel="折數填寫說明" />
  </span>
);

DiscountRateLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

function getLineProductOptions(productOptions, lines, index) {
  const currentId = lines?.[index]?.productId;
  const taken = new Set(
    (lines ?? [])
      .map((line, lineIndex) => (lineIndex === index ? null : line.productId))
      .filter(Boolean),
  );
  return productOptions.filter((option) => (
    !taken.has(option.value) || option.value === currentId
  ));
}

const LineRemoveButton = ({ index, onRemove, ariaLabel }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
    aria-label={ariaLabel}
    onClick={() => onRemove(index)}
  >
    <PiTrashBold className="size-4" />
  </Button>
);

LineRemoveButton.propTypes = {
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const CustomerOrderLineRow = ({
  index,
  control,
  register,
  errors,
  setValue,
  onRemove,
  productOptions,
  isProductSelectDisabled,
  productNoOptionsMessage,
}) => {
  const productIdField = `lines.${index}.productId`;
  const salesUnitNameId = `lines.${index}.salesUnitName`;
  const orderQtyId = `lines.${index}.orderQty`;
  const cancelQtyId = `lines.${index}.cancelQty`;
  const unitPriceId = `lines.${index}.unitPrice`;
  const discountRateId = `lines.${index}.discountRate`;
  const discountAmountId = `lines.${index}.discountAmount`;
  const remarkId = `lines.${index}.remark`;

  const { field: productField } = useController({
    name: productIdField,
    control,
    defaultValue: '',
  });

  const productError = getNestedError(errors, productIdField);
  const salesUnitNameError = getNestedError(errors, salesUnitNameId);
  const orderQtyError = getNestedError(errors, orderQtyId);
  const cancelQtyError = getNestedError(errors, cancelQtyId);
  const unitPriceError = getNestedError(errors, unitPriceId);
  const discountRateError = getNestedError(errors, discountRateId);
  const discountAmountError = getNestedError(errors, discountAmountId);
  const remarkError = getNestedError(errors, remarkId);

  function handleProductChange(nextId) {
    const option = productOptions.find((item) => item.value === nextId);
    setValue(`lines.${index}.salesUnitName`, option?.salesUnitName ?? '', {
      shouldDirty: true,
    });
  }

  const removeAriaLabel = `移除明細 ${index + 1}`;

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="flex min-w-0 items-center justify-between gap-2 @3xl/form-section:hidden">
        <p className="text-sm font-medium tabular-nums text-gray-700">
          {`項目# ${index + 1}.`}
        </p>
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
      <div className="hidden min-w-0 @3xl/form-section:block">
        <p className="text-center text-sm font-medium tabular-nums text-gray-700">
          {index + 1}
          .
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>產品</p>
        <SelectCore
          id={productIdField}
          options={productOptions}
          value={productField.value}
          onChange={(nextValue) => {
            productField.onChange(nextValue);
            handleProductChange(nextValue);
          }}
          onBlur={productField.onBlur}
          isDisabled={isProductSelectDisabled}
          noOptionsMessage={productNoOptionsMessage}
          errors={errors}
          hideErrorMessage
          aria-label={`產品 ${index + 1}`}
          aria-invalid={productError ? true : undefined}
          title={productError?.message || undefined}
          className="w-full"
        />
      </div>
      <div className={MOBILE_HALF_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>銷售單位</p>
        <Input
          id={salesUnitNameId}
          type="text"
          disabled
          aria-label={`銷售單位 ${index + 1}`}
          aria-invalid={salesUnitNameError ? true : undefined}
          title={salesUnitNameError?.message || undefined}
          className={LINE_INPUT_CLASS}
          {...register(salesUnitNameId)}
        />
      </div>
      <div className={MOBILE_FIELD_PAIR_CLASS}>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>訂購數量</p>
          <Input
            id={orderQtyId}
            type="number"
            min={0}
            step="any"
            aria-label={`訂購數量 ${index + 1}`}
            aria-invalid={orderQtyError ? true : undefined}
            title={orderQtyError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(orderQtyId)}
          />
        </div>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>取消量</p>
          <Input
            id={cancelQtyId}
            type="number"
            min={0}
            step="any"
            aria-label={`取消量 ${index + 1}`}
            aria-invalid={cancelQtyError ? true : undefined}
            title={cancelQtyError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(cancelQtyId)}
          />
        </div>
      </div>
      <div className={MOBILE_HALF_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>單價</p>
        <Input
          id={unitPriceId}
          type="number"
          min={0}
          step="any"
          aria-label={`單價 ${index + 1}`}
          aria-invalid={unitPriceError ? true : undefined}
          title={unitPriceError?.message || undefined}
          className={LINE_INPUT_CLASS}
          {...register(unitPriceId)}
        />
      </div>
      <div className={MOBILE_FIELD_PAIR_CLASS}>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>
            <DiscountRateLabel>折數（九折填 0.9）</DiscountRateLabel>
          </p>
          <Input
            id={discountRateId}
            type="number"
            min={0}
            step="any"
            aria-label={`折數 ${index + 1}`}
            aria-invalid={discountRateError ? true : undefined}
            title={discountRateError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(discountRateId)}
          />
        </div>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>每件折扣（與折數擇一）</p>
          <Input
            id={discountAmountId}
            type="number"
            min={0}
            step="any"
            aria-label={`每件折扣 ${index + 1}`}
            aria-invalid={discountAmountError ? true : undefined}
            title={discountAmountError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(discountAmountId)}
          />
        </div>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>行備註</p>
        <Textarea
          id={remarkId}
          rows={3}
          aria-label={`行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className={LINE_REMARK_TEXTAREA_CLASS}
          {...register(remarkId)}
        />
      </div>
      <div className="hidden min-w-0 @3xl/form-section:flex @3xl/form-section:justify-center">
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
    </li>
  );
};

CustomerOrderLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  productOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    salesUnitName: PropTypes.string,
  })).isRequired,
  isProductSelectDisabled: PropTypes.bool,
  productNoOptionsMessage: PropTypes.string,
};

const CustomerOrderManualLineRow = ({
  index,
  register,
  errors,
  onRemove,
}) => {
  const itemNameId = `manualLines.${index}.itemName`;
  const unitNameId = `manualLines.${index}.unitName`;
  const orderQtyId = `manualLines.${index}.orderQty`;
  const cancelQtyId = `manualLines.${index}.cancelQty`;
  const unitPriceId = `manualLines.${index}.unitPrice`;
  const remarkId = `manualLines.${index}.remark`;

  const itemNameError = getNestedError(errors, itemNameId);
  const unitNameError = getNestedError(errors, unitNameId);
  const orderQtyError = getNestedError(errors, orderQtyId);
  const cancelQtyError = getNestedError(errors, cancelQtyId);
  const unitPriceError = getNestedError(errors, unitPriceId);
  const remarkError = getNestedError(errors, remarkId);

  const removeAriaLabel = `移除其他產品 ${index + 1}`;

  return (
    <li className={MANUAL_LINE_ROW_CLASS}>
      <div className="flex min-w-0 items-center justify-between gap-2 @3xl/form-section:hidden">
        <p className="text-sm font-medium tabular-nums text-gray-700">
          {`項目# ${index + 1}.`}
        </p>
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
      <div className="hidden min-w-0 @3xl/form-section:block">
        <p className="text-center text-sm font-medium tabular-nums text-gray-700">
          {index + 1}
          .
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>品名</p>
        <Input
          id={itemNameId}
          type="text"
          aria-label={`其他產品品名 ${index + 1}`}
          aria-invalid={itemNameError ? true : undefined}
          title={itemNameError?.message || undefined}
          className={LINE_INPUT_CLASS}
          {...register(itemNameId)}
        />
      </div>
      <div className={MOBILE_HALF_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>單位</p>
        <Input
          id={unitNameId}
          type="text"
          aria-label={`其他產品單位 ${index + 1}`}
          aria-invalid={unitNameError ? true : undefined}
          title={unitNameError?.message || undefined}
          className={LINE_INPUT_CLASS}
          {...register(unitNameId)}
        />
      </div>
      <div className={MOBILE_FIELD_PAIR_CLASS}>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>訂購數量</p>
          <Input
            id={orderQtyId}
            type="number"
            min={0}
            step="any"
            aria-label={`其他產品訂購數量 ${index + 1}`}
            aria-invalid={orderQtyError ? true : undefined}
            title={orderQtyError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(orderQtyId)}
          />
        </div>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>取消量</p>
          <Input
            id={cancelQtyId}
            type="number"
            min={0}
            step="any"
            aria-label={`其他產品取消量 ${index + 1}`}
            aria-invalid={cancelQtyError ? true : undefined}
            title={cancelQtyError?.message || undefined}
            className={LINE_INPUT_CLASS}
            {...register(cancelQtyId)}
          />
        </div>
      </div>
      <div className={MOBILE_HALF_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>單價</p>
        <Input
          id={unitPriceId}
          type="number"
          min={0}
          step="any"
          aria-label={`其他產品單價 ${index + 1}`}
          aria-invalid={unitPriceError ? true : undefined}
          title={unitPriceError?.message || undefined}
          className={LINE_INPUT_CLASS}
          {...register(unitPriceId)}
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>行備註</p>
        <Textarea
          id={remarkId}
          rows={3}
          aria-label={`其他產品行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className={LINE_REMARK_TEXTAREA_CLASS}
          {...register(remarkId)}
        />
      </div>
      <div className="hidden min-w-0 @3xl/form-section:flex @3xl/form-section:justify-center">
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
    </li>
  );
};

CustomerOrderManualLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  onRemove: PropTypes.func.isRequired,
};

const CustomerOrderLinesEditor = ({
  control,
  register,
  errors,
  setValue,
  productOptions = [],
  isProductSelectDisabled = false,
  productNoOptionsMessage = '請先選擇客戶',
  canAddLine = true,
}) => {
  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'lines',
  });
  const {
    fields: manualFields,
    append: appendManual,
    remove: removeManual,
  } = useFieldArray({
    control,
    name: 'manualLines',
  });
  const lines = useWatch({ control, name: 'lines', defaultValue: [] });

  const selectedProductCount = useMemo(() => {
    const ids = new Set((lines ?? []).map((line) => line.productId).filter(Boolean));
    return ids.size;
  }, [lines]);

  const isProductPoolFull = productOptions.length > 0
    && selectedProductCount >= productOptions.length;

  return (
    <div className="space-y-8">
      <section className="@container/form-section pb-5">
        <div className="form-section__header">
          <h2 className="form-block__title mb-0!">訂單明細</h2>
          <Button
            type="button"
            size="sm"
            disabled={!canAddLine || isProductPoolFull}
            onClick={() => append(createEmptyCustomerOrderLine())}
          >
            <PiPlusBold className="size-3.5" />
            新增明細
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {CUSTOMER_ORDER_LINES_TIP}
        </p>

        <div className={cn('mt-3', PANEL_MAX_H, 'overflow-y-auto')}>
          <div className="overflow-x-scroll rounded border border-gray-400/65">
            <div className={LINES_MIN_W}>
              <div className={LINE_HEADER_CLASS}>
                <span className="text-center">項目#</span>
                <span>產品</span>
                <span>銷售單位</span>
                <span>訂購數量</span>
                <span>取消量</span>
                <span>單價</span>
                <span>
                  <DiscountRateLabel>折數</DiscountRateLabel>
                </span>
                <span>每件折扣</span>
                <span>行備註</span>
                <span className="text-center">操作</span>
              </div>
              <ul className="m-0 list-none divide-y divide-dashed divide-gray-400/65 p-0">
                {fields.length === 0 ? (
                  <li className="px-3 py-8 text-sm text-muted-foreground">
                    尚無產品明細（可僅用其他產品）
                  </li>
                ) : (
                  fields.map((field, index) => (
                    <CustomerOrderLineRow
                      key={field.id}
                      index={index}
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      onRemove={remove}
                      productOptions={getLineProductOptions(productOptions, lines, index)}
                      isProductSelectDisabled={isProductSelectDisabled}
                      productNoOptionsMessage={productNoOptionsMessage}
                    />
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="@container/form-section pb-5">
        <div className="form-section__header">
          <h2 className="form-block__title mb-0!">其他產品</h2>
          <Button
            type="button"
            size="sm"
            disabled={!canAddLine}
            onClick={() => appendManual(createEmptyCustomerOrderManualLine())}
          >
            <PiPlusBold className="size-3.5" />
            新增其他產品
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {CUSTOMER_ORDER_MANUAL_LINES_TIP}
        </p>

        <div className={cn('mt-3', PANEL_MAX_H, 'overflow-y-auto')}>
          <div className="overflow-x-scroll rounded border border-gray-400/65">
            <div className={MANUAL_LINES_MIN_W}>
              <div className={MANUAL_LINE_HEADER_CLASS} aria-hidden>
                <span className="text-center">項目#</span>
                <span>品名</span>
                <span>單位</span>
                <span>訂購數量</span>
                <span>取消量</span>
                <span>單價</span>
                <span>行備註</span>
                <span className="text-center">操作</span>
              </div>
              <ul className="m-0 list-none divide-y divide-dashed divide-gray-400/65 p-0">
                {manualFields.length === 0 ? (
                  <li className="px-3 py-8 text-sm text-muted-foreground">
                    尚無其他產品
                  </li>
                ) : (
                  manualFields.map((field, index) => (
                    <CustomerOrderManualLineRow
                      key={field.id}
                      index={index}
                      register={register}
                      errors={errors}
                      onRemove={removeManual}
                    />
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

CustomerOrderLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  productOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    salesUnitName: PropTypes.string,
  })),
  isProductSelectDisabled: PropTypes.bool,
  productNoOptionsMessage: PropTypes.string,
  canAddLine: PropTypes.bool,
};

export default CustomerOrderLinesEditor;
